import React, { useEffect, useState } from 'react';
import { 
  Container,
  Typography, 
  Box, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  CircularProgress, 
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Grid,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import api from '../services/api';

const RealestateIntegration: React.FC = () => {
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const navigate = useNavigate();
  const { agency, refreshAuth } = useAuth();
  const [apiUrl, setApiUrl] = useState('');
  const [agentId, setAgentId] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [feedStatus, setFeedStatus] = useState<any>(null);
  const [loadingFeedStatus, setLoadingFeedStatus] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showWizard, setShowWizard] = useState(false);
  const [isEditingAgentId, setIsEditingAgentId] = useState(false);
  const [agencyDetails, setAgencyDetails] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    state: ''
  });
  const [savingAgencyDetails, setSavingAgencyDetails] = useState(false);
  const [agencyDetailsSuccess, setAgencyDetailsSuccess] = useState('');
  const [agencyDetailsError, setAgencyDetailsError] = useState('');

  const australianStates = [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'SA', label: 'South Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'ACT', label: 'Australian Capital Territory' },
    { value: 'NT', label: 'Northern Territory' },
  ];

  // Helper function to normalize state value to match dropdown options
  const normalizeState = (stateValue: string | undefined): string => {
    if (!stateValue) return '';
    
    // If it's already a valid code, return it
    const validCode = australianStates.find(s => s.value === stateValue.toUpperCase());
    if (validCode) return validCode.value;
    
    // Try to match by label (full name)
    const matchedByLabel = australianStates.find(s => 
      s.label.toLowerCase() === stateValue.toLowerCase()
    );
    if (matchedByLabel) return matchedByLabel.value;
    
    // Try partial match (e.g., "New South Wales" -> "NSW")
    const partialMatch = australianStates.find(s => 
      s.label.toLowerCase().includes(stateValue.toLowerCase()) ||
      stateValue.toLowerCase().includes(s.label.toLowerCase())
    );
    if (partialMatch) return partialMatch.value;
    
    return stateValue.toUpperCase(); // Return as-is if no match
  };

  useEffect(() => {
    if (!agency?.slug) return;
    
    const fetchAgencyDetails = async () => {
      try {
        const response = await api.get(`/agencies/${agency.slug}`);
        const agencyData = response.data;
        setAgencyDetails({
          name: agencyData.name || '',
          email: agencyData.email || '',
          phone: agencyData.phone || '',
          address: agencyData.address || '',
          state: normalizeState(agencyData.state)
        });
      } catch (err: any) {
        console.error('Error loading agency details:', err);
        // Fallback to agency from auth if API call fails
        if (agency) {
          setAgencyDetails({
            name: agency.name || '',
            email: agency.email || '',
            phone: agency.phone || '',
            address: agency.address || '',
            state: normalizeState(agency.state)
          });
        }
      }
    };
    
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/agencies/${agency.slug}/realestate-config`);
        const config = response.data.config;
        if (config) {
          setApiUrl(config.apiUrl || '');
          setAgentId(config.agentId || '');
          setIsEditingAgentId(!config.agentId);
          
          // Show wizard if agentId is not configured
          if (!config.agentId) {
            setShowWizard(true);
          }
        } else {
          setShowWizard(true);
          setIsEditingAgentId(true);
        }
      } catch (err: any) {
        // Config might not exist yet, that's okay
        if (err.response?.status !== 404) {
          console.error('Error loading realestate config:', err);
        } else {
          setShowWizard(true);
          setIsEditingAgentId(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAgencyDetails();
    fetchConfig();
    fetchFeedStatus();
  }, [agency?.slug]);

  const fetchFeedStatus = async () => {
    if (!agency?.slug) return;
    try {
      setLoadingFeedStatus(true);
      const response = await api.get(`/agencies/${agency.slug}/realestate-feed-status`);
      setFeedStatus(response.data);
    } catch (err: any) {
      // Feed status might not be available yet
      console.error('Error loading feed status:', err);
    } finally {
      setLoadingFeedStatus(false);
    }
  };

  const handleOpenREAAgentAdmin = () => {
    // Open REA Agent Admin login page
    window.open('https://www.realestate.com.au/agentadmin/', '_blank');
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess('');
    setError('');
    
    if (!agentId || !agentId.trim()) {
      setError('REA Agency ID is required');
      setSaving(false);
      return;
    }

    if (!agency?.slug) {
      setError('Agency details are missing. Please refresh and try again.');
      setSaving(false);
      return;
    }
    
    try {
      await api.put(`/agencies/${agency.slug}/realestate-config`, {
        apiUrl: apiUrl || undefined,
        agentId: agentId.trim(),
      });
      
      setSuccess('Realestate.com.au configuration saved successfully!');
      setShowWizard(false);
      setIsEditingAgentId(false);
      // Refresh feed status after saving
      setTimeout(() => {
        fetchFeedStatus();
      }, 1000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save realestate.com.au configuration');
    } finally {
      setSaving(false);
    }
  };

  if (!agency) return null;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Card>
          <CardContent>
            <CircularProgress />
          </CardContent>
        </Card>
      </Container>
    );
  }

  const steps = [
    {
      label: 'Enter your office details',
      description: 'Please confirm your agency information is correct. Update any fields if needed.',
    },
    {
      label: 'Enable Workflow.Estate in REA Agent Admin',
      description: 'Complete the "Change my uploader" form in your REA Agent Admin account to enable Workflow.Estate.',
    },
    {
      label: 'Enter your REA Agency ID',
      description: 'After REA enables Workflow.Estate on your account, enter your REA Agency ID (Agent ID) to complete the integration.',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate(`/agencies/${agencySlug}/third-party-integrations`)}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Realestate.com.au Integration
        </Typography>
      </Box>

      {showWizard && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                REA Integration Setup Wizard
              </Typography>
              <Button size="small" onClick={() => setShowWizard(false)}>
                Skip Wizard
              </Button>
            </Box>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel>{step.label}</StepLabel>
                  <StepContent>
                    {typeof step.description === 'string' ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {step.description}
                      </Typography>
                    ) : (
                      <Box sx={{ mb: 2 }}>
                        {step.description}
                      </Box>
                    )}
                    {index === 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                        <TextField
                          label="Agency Name"
                          value={agencyDetails.name}
                          onChange={e => setAgencyDetails({ ...agencyDetails, name: e.target.value })}
                          fullWidth
                          required
                          helperText="Your agency name as it appears on realestate.com.au"
                        />
                        <TextField
                          label="Email"
                          type="email"
                          value={agencyDetails.email}
                          onChange={e => setAgencyDetails({ ...agencyDetails, email: e.target.value })}
                          fullWidth
                          required
                          helperText="Primary contact email address"
                        />
                        <TextField
                          label="Phone"
                          value={agencyDetails.phone}
                          onChange={e => setAgencyDetails({ ...agencyDetails, phone: e.target.value })}
                          fullWidth
                          required
                          helperText="Primary contact phone number"
                        />
                        <TextField
                          label="Office Address"
                          value={agencyDetails.address}
                          onChange={e => setAgencyDetails({ ...agencyDetails, address: e.target.value })}
                          fullWidth
                          required
                          multiline
                          rows={2}
                          helperText="Your office address"
                        />
                        <FormControl fullWidth required>
                          <InputLabel id="state-label">State</InputLabel>
                          <Select
                            labelId="state-label"
                            id="state"
                            label="State"
                            value={agencyDetails.state || ''}
                            onChange={e => setAgencyDetails({ ...agencyDetails, state: e.target.value })}
                          >
                            {australianStates.map((state) => (
                              <MenuItem key={state.value} value={state.value}>
                                {state.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                          <Button
                            variant="contained"
                            onClick={async () => {
                              if (!agency?.slug) return;
                              setSavingAgencyDetails(true);
                              setAgencyDetailsSuccess('');
                              setAgencyDetailsError('');
                              
                              try {
                                await api.put(`/agencies/${agency.slug}`, {
                                  name: agencyDetails.name,
                                  email: agencyDetails.email,
                                  phone: agencyDetails.phone,
                                  address: agencyDetails.address,
                                  state: agencyDetails.state,
                                });
                                setAgencyDetailsSuccess('Agency details updated successfully!');
                                // Refresh auth to get updated agency data
                                await refreshAuth();
                                setTimeout(() => {
                                  setActiveStep(1);
                                }, 1000);
                              } catch (err: any) {
                                setAgencyDetailsError(err.response?.data?.message || 'Failed to update agency details');
                              } finally {
                                setSavingAgencyDetails(false);
                              }
                            }}
                            disabled={savingAgencyDetails || !agencyDetails.name || !agencyDetails.email || !agencyDetails.phone || !agencyDetails.address}
                          >
                            {savingAgencyDetails ? 'Saving...' : 'Save & Continue'}
                          </Button>
                        </Box>
                        {agencyDetailsSuccess && (
                          <Alert severity="success">{agencyDetailsSuccess}</Alert>
                        )}
                        {agencyDetailsError && (
                          <Alert severity="error">{agencyDetailsError}</Alert>
                        )}
                      </Box>
                    )}
                    {index === 1 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                        <Alert severity="info">
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Follow these steps in your REA Agent Admin account:</strong>
                          </Typography>
                          <Box component="ol" sx={{ pl: 3, m: 0 }}>
                            <li>Log into your <a href="https://agentadmin.realestate.com.au/" target="_blank" rel="noopener noreferrer">realestate.com.au Agent Admin account</a>.</li>
                            <li>Click <strong>"XML Uploads"</strong> from the left-hand menu.</li>
                            <li>Click the <strong>"Change my uploader"</strong> button.</li>
                            <li>Select <strong>"Listing Upload API"</strong> from the <strong>"What will this uploader be used for?"</strong> drop-down list.</li>
                            <li>Select <strong>"Codepulse PTY Ltd"</strong> in the <strong>"Who is your new provider?"</strong> drop-down list.</li>
                            <li>Complete and submit the form.</li>
                          </Box>
                          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                            <strong>Note:</strong> If "Codepulse PTY Ltd" is not yet in the "Who is your new provider?" drop-down list, please choose the first option and type "Workflow.Estate" in the "More Info" section.
                          </Typography>
                        </Alert>
                        <Button
                          variant="contained"
                          startIcon={<LinkIcon />}
                          onClick={() => window.open('https://agentadmin.realestate.com.au/', '_blank')}
                          sx={{ mb: 2 }}
                        >
                          Open REA Agent Admin
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          After completing the form, REA's Fulfilment Solutions team will enable Workflow.Estate on your account. 
                          Once enabled, proceed to the next step to enter your REA Agency ID.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          <strong>Need help?</strong> See{' '}
                          <a 
                            href="https://help.realestate.com.au/hc/en-us/articles/48887137885465-How-to-change-your-listing-uploader-or-CRM" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', textDecoration: 'underline' }}
                          >
                            REA's guide on how to change your listing uploader or CRM
                          </a>
                          {' '}for detailed instructions.
                        </Typography>
                      </Box>
                    )}
                    {index === 2 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Enter your REA Agency ID (Agent ID)</strong>
                          </Typography>
                          <Typography variant="body2">
                            Your REA Agency ID is provided by REA after they enable Workflow.Estate on your account. 
                            This ID is required to post listings to realestate.com.au on behalf of your agency.
                          </Typography>
                        </Alert>
                        <TextField
                          label="REA Agency ID (Agent ID)"
                          value={agentId}
                          onChange={e => setAgentId(e.target.value)}
                          fullWidth
                          required
                          helperText="Enter the REA Agency ID provided by REA's Fulfilment Solutions team"
                          InputProps={{
                            readOnly: !isEditingAgentId,
                            endAdornment: !isEditingAgentId ? (
                              <InputAdornment position="end">
                                <Tooltip title="Edit REA Agency ID">
                                  <IconButton
                                    edge="end"
                                    aria-label="Edit REA Agency ID"
                                    onClick={() => setIsEditingAgentId(true)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ) : undefined
                          }}
                          sx={{ mb: 2 }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleSave}
                          disabled={saving || !agentId || !agentId.trim() || !isEditingAgentId}
                          sx={{ mb: 2 }}
                        >
                          {saving ? 'Saving...' : 'Save & Complete Integration'}
                        </Button>
                        {success && (
                          <Alert severity="success">{success}</Alert>
                        )}
                        {error && (
                          <Alert severity="error">{error}</Alert>
                        )}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      {index > 0 && (
                        <Button
                          variant="outlined"
                          onClick={() => setActiveStep(index - 1)}
                        >
                          Back
                        </Button>
                      )}
                      {index === 1 && (
                        <Button
                          variant="contained"
                          onClick={() => setActiveStep(2)}
                        >
                          REA has enabled Workflow.Estate - Continue
                        </Button>
                      )}
                      {index < steps.length - 1 && index !== 0 && index !== 1 && index !== 2 && (
                        <Button
                          variant="contained"
                          onClick={() => setActiveStep(index + 1)}
                        >
                          Continue
                        </Button>
                      )}
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>
      )}

      {!showWizard && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enable Workflow.Estate on your realestate.com.au account to enable automatic listing posting. 
              Workflow.Estate is an authorized Partner Platform partner - partner credentials are configured system-wide.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>To enable Workflow.Estate on your REA account:</strong>
              </Typography>
              <Box component="ol" sx={{ pl: 3, m: 0 }}>
                <li>Log into your realestate.com.au Agent Admin account</li>
                <li>Click <strong>"XML Uploads"</strong> from the left-hand menu</li>
                <li>Click the <strong>"Change my uploader"</strong> button</li>
                <li>Select the relevant API from the <strong>"What will this uploader be used for?"</strong> drop-down list</li>
                <li>Select <strong>"Codepulse PTY Ltd"</strong> in the <strong>"Who is your new provider?"</strong> drop-down list</li>
                <li>Complete and submit the form</li>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                <strong>Note:</strong> If you cannot find "Workflow.Estate" in the dropdown, select the first option and type "Workflow.Estate" in the "More Info" section.
              </Typography>
            </Alert>
            
            <Box sx={{ mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={handleOpenREAAgentAdmin}
                sx={{ mb: 2 }}
              >
                Open REA Agent Admin
              </Button>
              <Typography variant="caption" display="block" color="text.secondary">
                After completing the form, REA's Fulfilment Solutions team will enable Workflow.Estate on your account. 
                Once enabled, listings will automatically sync to realestate.com.au.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Need help?</strong> See{' '}
                <a 
                  href="https://help.realestate.com.au/hc/en-us/articles/48887137885465-How-to-change-your-listing-uploader-or-CRM" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  REA's guide on how to change your listing uploader or CRM
                </a>
                {' '}for detailed instructions.
              </Typography>
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <TextField
                label="REA Agency ID (Agent ID)"
                value={agentId}
                onChange={e => setAgentId(e.target.value)}
                fullWidth
                required
                helperText="Enter the REA Agency ID provided by REA's Fulfilment Solutions team after they enable Workflow.Estate on your account"
                InputProps={{
                  readOnly: !isEditingAgentId,
                  endAdornment: !isEditingAgentId ? (
                    <InputAdornment position="end">
                      <Tooltip title="Edit REA Agency ID">
                        <IconButton
                          edge="end"
                          aria-label="Edit REA Agency ID"
                          onClick={() => setIsEditingAgentId(true)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : undefined
                }}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !agentId || !agentId.trim() || !isEditingAgentId}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
              )}
            </Box>
            
            <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> Workflow.Estate is an authorized Partner Platform partner. 
                Partner credentials are configured system-wide. Once you complete the "Change my uploader" form in REA Agent Admin, 
                REA's Fulfilment Solutions team will enable the integration on your account.
                Visit{' '}
                <a 
                  href="https://help.realestate.com.au/hc/en-us/articles/48886865648665-A-guide-to-data-integrations-in-Ignite" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  REA Partner Platform documentation
                </a>
                {' '}for more information.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Feed Status Dashboard */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Listing Feed Status
            </Typography>
            <Tooltip title="Refresh feed status">
              <IconButton onClick={fetchFeedStatus} disabled={loadingFeedStatus}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          {loadingFeedStatus ? (
            <CircularProgress />
          ) : feedStatus ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Last Successful Feed
                  </Typography>
                  <Typography variant="h6">
                    {feedStatus.lastSuccessfulFeed 
                      ? new Date(feedStatus.lastSuccessfulFeed).toLocaleString()
                      : 'Never'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Properties Queued
                  </Typography>
                  <Typography variant="h6">
                    {feedStatus.propertiesQueued || 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Errors from REA
                </Typography>
                {feedStatus.errors && feedStatus.errors.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {feedStatus.errors.map((error: any, index: number) => (
                      <Chip
                        key={index}
                        label={error.message || error}
                        color="error"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No errors
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: feedStatus.invalidMedia > 0 ? 'error.light' : 'background.paper' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Invalid Media
                  </Typography>
                  <Typography variant="h6">
                    {feedStatus.invalidMedia || 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: feedStatus.mismatchedSuburb > 0 ? 'warning.light' : 'background.paper' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Mismatched Suburb/State
                  </Typography>
                  <Typography variant="h6">
                    {feedStatus.mismatchedSuburb || 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: feedStatus.missingAgentId > 0 ? 'warning.light' : 'background.paper' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Missing Agent ID
                  </Typography>
                  <Typography variant="h6">
                    {feedStatus.missingAgentId || 0}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Feed status will appear here once your credentials are configured and validated.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default RealestateIntegration;

